import React, {ReactElement, SyntheticEvent} from 'react';
import {connect} from "react-redux";
import './TransactionCard.less';
import '../../stylesheets/form.less';
import Transaction from "../../entities/transaction";
import util from "../../lib/util";
import OkCancel from "../../ui/Buttons/OkCancel";
import Matter from "../../entities/matter";
import CaseHelper from "../../Case/CaseHelper";
import IconButton from "../../ui/IconButton/IconButton";
import Icons from "../../ui/IconButton/Icons";
import LookupRegistry from "../../ui/LookupSelect/LookupRegistry";
import FinancialHelper from "../FinancialHelper";
import i18n from '../../i18n/i18n';
import {isBalanceAdjustment, isEscrow, isPayment, isTransfer, isWithdrawal} from "./TransactionHelper";
import {formatFMLFromId, formatLFM, sortContacts} from "../../Contacts/ContactFormat";
import {getParticipants} from "../../lib/picos/participantHelper";
import type {RootState} from "../../@types/store";
import type {SelectOption} from "../../@types/select";
import {uuid} from "../../@types/entity";
import HelpMessage from "../../i18n/HelpMessage";
import {TransactionChildCardProps} from "./TransactionCard";

/**
 * See documentation for the Transaction schema (schemas/transaction.js) for a full rundown on how the Transaction works, and which
 * bits are used for each type of transaction.
 *
 * This card can be called from either an EditableTable or directly from a button press.  The env and startEditing props are defaulted
 * so that coming in from outside of an EditableTable still works.
 *
 *
 *
 * There are two ways that this card is shown: from an EditableTable of transactions in Financials, and directly from a Matter or Contact.
 * Check props.env.matter and props.env.contact to see if either of these cases are true.
 *
 * If we are called from a Matter, we will assume that the matter is unchangeable, but the contact (if > 1) is.
 * If we are called from a Contact, we will assume that the contact is unchangeable, but the Matter is.
 *
 * For a payment, we show a contact first, then list their cases. Click on the cases that we will apply the funds to
 */
function EscrowCard(props: EscrowCardProps) {
    // OWA-552: guarantee a non-null `current` transaction in code, not via a prop-type
    // contract — this card never depends on the parent always passing one. currentTx is the
    // parent's live current in practice; the fallback only materialises on the warned path.
    if (!props.current && import.meta.env.DEV) console.warn('<EscrowCard/> rendered without a current Transaction; using a blank fallback.');
    const currentTx = props.current ?? new Transaction({});

    /**
     * Get trust accounts for the clients and matters.
     * There will be one trust per matter, and one trust for each client in common to all matters.
     *
     * However, if this is a payment, and there are multiple matters selected, there are three options:
     * 1. Put each distribution into the related client trust
     * 2. Put the whole amount into the client trust.
     * 3. Put the whole amount into
     *
     * Payments: trusts are used as a target (payment is always an external entity).
     *
     * Need to look at the 'field' parameter (to/from) to make sure we are not allowing the same to/from funding source.
     *
     * @param {string[]} types
     * @param {'to'|'from'} [field]
     * @return {Array.<{label: string, value: uuid, data: object}>}
     */
    const getTrustAccounts = (types: string[], field: 'to' | 'from' = 'from'): SelectOption<string, ResultData>[] => {
        const tr = currentTx;

        // Get the related matters
        let matters: Matter[];

        // If this is a transfer, we need to get all matters for the client
        if (isTransfer(tr.txType)) matters = CaseHelper.getMattersForClients(tr.clientIds);

        // Otherwise get matters specific to the case
        else matters = props.matter
                       ? [props.matter]
                       : currentTx.matterIds.map(mid => CaseHelper.getMatterFromId(mid));

        // Get an array of clientId arrays [[],[],...]
        // let clientIds = matters.map(m => m.clients);

        // If more than one result, get a list of Ids that are in common to each of the matters
        // if (matters.length > 1) clientIds = clientIds.reduce((a, b) => a.filter(c => b.includes(c)), []);
        // else clientIds = clientIds.flat();

        // If not clients in common, we can not pull from any client trusts.
        const clientTrusts: ResultData[] = types.includes('G')
                                           ? currentTx.clientIds
                                               .filter(cid => field === 'from' && (isPayment(props.txType) || isTransfer(props.txType))
                                                              ? FinancialHelper.getTrustAmount('G', cid) > 0
                                                              : true)
                                               .map(cid => {
                                                   // Get the balance for this trust
                                                   const contact = props.contacts.get(cid);
                                                   const clientName = formatLFM(contact, true);
                                                   const trustBalance = FinancialHelper.getTrustAmount('G', cid);

                                                   return {
                                                       id: cid,
                                                       type: 'G',
                                                       name: FinancialHelper.formatLabel(Icons.USER, i18n.cvs('trustFor', {value: clientName}), trustBalance, 'blue'),
                                                       balance: trustBalance
                                                   };
                                               })
                                           : [];

        // Add the trust for the matter
        const matterTrusts: ResultData[] = types.includes('M') ? matters
            .filter(m => field === 'from' && ['PT', 'PC', 'TT'].includes(props.txType)
                         ? FinancialHelper.getTrustAmount('M', m.id) > 0
                         : true)
            .map(matter => {
                const trustAmount = FinancialHelper.getTrustAmount('M', matter.id);

                return {
                    id: matter.id,
                    type: 'M',
                    name: FinancialHelper.formatLabel(Icons.CASE, i18n.cvs('trustFor', {value: matter.name}), trustAmount, 'brown'),
                    balance: trustAmount
                };
            }) : [];

        const merged: ResultData[] = props.matter ? [...matterTrusts, ...clientTrusts] : [...clientTrusts, ...matterTrusts]
            .filter(obj => {
                // source and target can not be the same!
                const id = field === 'from' ? currentTx.target : currentTx.source;

                // source has to have at least as much funds as the amount
                const enough = field === 'from' ? obj.balance >= (currentTx.amount || 0) : true;
                return (obj.id !== id) && enough;
            });

        return merged.map(account => {
            return {
                label: account.name,
                value: account.id,
                data: account
            };
        });
    };

    /**
     * Get bank accounts in Select format.  If a type is given ([T]rust/[O]perating), limit to that type.
     *
     * @param {string[]} [types]
     */
    const getBankAccounts = (types: string[]): SelectOption<string, ResultData>[] => {

        // Filter to see which bank accounts to show
        const filter = ba => {
            // Only include accounts of the given types
            if (types && !types.includes(ba.accountType)) return false;

            // If this is an escrow account, match the matterId with the transaction's matter Ids
            if (ba.accountType === 'E') return currentTx.matterIds.includes(ba.matterId);

            return true;
        };

        return props.bankAccounts
            .filter(filter)
            .map(ba => {
                const icon = ba.accountType === 'T' ? Icons.SCALES : ba.accountType === 'O' ? Icons.BANK : Icons.ESCROW;
                return {
                    // FIXME: What should be the balance? Also in name below.
                    label: FinancialHelper.formatLabel(icon, ba.name, 0),
                    value: ba.id,
                    data: {
                        id: ba.id,
                        type: ba.accountType, // 'B'
                        name: FinancialHelper.formatLabel(icon, ba.name, 0),
                        // @ts-expect-error: This is all messed up
                        balance: FinancialHelper.getTrustAmount(ba) // TODO: This is almost definitely wrong
                    } as ResultData
                };
            });
    };

    /**
     * Get a list of bank accounts and trust accounts.  If client(s) or a matter are selected, we can show the trust for each of them.
     * @param {Transaction} tr - the current transaction
     * @return {object[]} - formatted objects with label, value and data
     */
    const getSourceAccounts = (tr: Transaction): SelectOption<string, any> => {
        const txType = tr ? tr.txType : props.txType;
        let accounts;

        if (['FR', 'PC'].includes(txType)) accounts = getContacts(tr);
        if (isPayment(txType)) accounts = getPaymentAccounts(tr);
        else if (isWithdrawal(txType)) accounts = getWithdrawalAccounts(tr);
        else if (isTransfer(txType)) accounts = getTransferAccounts(tr);
        else if (isBalanceAdjustment(txType)) accounts = [];
        else if (isEscrow(txType)) accounts = [];
        else throw new Error(`txType of ${txType} not recognized`);

        if (tr) {
            return accounts.filter(at => at.data.id === tr.source);
        }
        else {
            return accounts;
        }
    };

    /**
     * Get a list of target accounts.
     *
     * If a transaction is passed, just return the object matching that transaction's target
     *
     * @param {Transaction} [tr]
     * @return {object[]} - formatted objects with label, value and data
     */
    const getTargetAccounts = (tr) => {
        const txType = tr ? tr.txType : props.txType;
        let accounts;
        if (isPayment(txType)) accounts = getPaymentAccounts(tr, 'to');
        else if (isWithdrawal(txType)) accounts = getPayees();
        else if (isTransfer(txType)) accounts = getTransferAccounts(tr, 'to');
        else if (isEscrow(txType)) accounts = getEscrowAccounts(tr);
        else if (isBalanceAdjustment(txType)) accounts = getPaymentAccounts(tr, 'to');
        // else throw new Error(`txType of ${txType} not recognized`);

        if (tr) {
            return accounts.filter(at => at.data.id === tr.target);
        }
        else {
            return accounts;
        }
    };

    /**
     * Payments are always made to a trust (client or matter), an operating account, or an Escrow account.
     *
     * List trust accounts for each client and matter, along with the Operating and Escrow accounts.
     *
     * If there are multiple matters selected, we will need to show only the client trust, no matter trusts
     *
     * @param {Transaction} tr
     * @param {'to'|'from'} [field]
     * @return {object[]} - formatted objects with label, value and data
     */
    const getPaymentAccounts = (tr: Transaction, field: 'to' | 'from' = 'from'): SelectOption<string, ResultData>[] => {
        let accountTypes = ['PC', 'PT'].includes(tr?.txType) ? ['O'] : [];
        let trustTypes = ['G', 'M']; // By default we show all client and matter trusts
        let showTrustAccounts = true; // Default show them, but if the
        let showBankAccounts = true;

        const tx = currentTx;

        // If there are matters entered, and the target type is [B]ank Account, and there are Bank Accounts
        // of type [O]perating, then we set types to [O]perating and hide trust accounts.
        if (tx.matterIds.length > 1 && tx.targetType === 'B' && getBankAccounts(['O'])) { // FIXME: Not sure about getBankAccounts(['O'])
            accountTypes = ['O']; // FIXME: Should this be trustTypes?
            showTrustAccounts = false;
        }

        // If there are matters entered, and the target type is [C]ontact, and there are clients entered,
        // set trustTypes to [C]ontact and showBankAccounts to false.
        if (tx.matterIds.length > 1 && tx.targetType === 'G' && tx.clientIds.length > 1) {
            trustTypes = ['G'];
            showBankAccounts = false;
        }

        // If no matters selected, show only general trusts
        if (tx.matterIds.length === 0) {
            trustTypes = ['G'];
        }

        const bankAccounts = showBankAccounts ? getBankAccounts(accountTypes) : [];
        const trustAccounts = showTrustAccounts ? getTrustAccounts(trustTypes, field) : [];

        return [...trustAccounts, ...bankAccounts];
    };

    /**
     * Get all contacts in the system, used for source type of 'C'.  Put the clients for this transaction at the top,
     * along with Benefactors.  This is only used to populate sources, not targets.
     * of the list.
     *
     * @param {Transaction} tr
     * @return {SelectOption<string, ResultData>[]} - formatted objects with label, value and data
     */
    const getContacts = (tr) => {
        const tx = currentTx;

        // Get the clients related to this client/matter
        const clients = tr?.clientIds || [];
        let matterClients = [];
        let matterBenefactors = [];

        if (tx.matterIds.length > 0) {
            const matters = tx.matterIds
                .map(mid => props.matters.find(m => mid === m.id));

            // Find clients for matters
            matterClients = matters
                .map(m => m.clients)
                .flat();

            matterBenefactors = matters
                .map(m => getParticipants(m.id))
                .flat()
                .filter(p => p.relationships?.includes(LookupRegistry.SACROSANCT.BENEFACTOR))
                .map(p => p.contactId);
        }

        // This is a list of targeted contacts to put at the top of the list
        const targetIds = clients.concat(matterBenefactors)
            .concat(matterClients)
            .filter(util.distinctFilter);

        const results = props.contacts
            .sort((a, b) => targetIds.includes(a.id) ? -1 : targetIds.includes(b.id) ? 1 : formatLFM(a).localeCompare(formatLFM(b)))
            .map(c => {
                const name = formatLFM(c);

                return {
                    label: name,
                    value: c.id,
                    data: {
                        id: c.id,
                        type: 'C',
                        name,
                        balance: 0
                    }
                };
            });

        if (tr) return results.filter(v => v.value === tr.source);
        else return results;
    };


    /**
     * A disbursement in this context has two forms:
     * 1. Funds from the client are used to pay a bill (client or matter trusts plus Escrow accounts),
     * 2. Money in the trust is being returned to the client (client or matter trusts plus Escrow accounts).
     *
     * The disbursement can be made to a specific person or company (Contact), NOT to another account!
     * That would be a transfer, not a disbursement.  So with a disbursement, the target will be a ContactId,
     * the targetType will be 'C', and the amount will be removed from the source account.
     *
     * List trust accounts for each client and matter, along with Escrow accounts.
     *
     * @param {Transaction} tr
     * @param {'to'|'from'} [field]
     * @return {object[]} - formatted objects with label, value and data
     */
    const getWithdrawalAccounts = (tr: Transaction, field: 'to' | 'from' = 'from') => {
        const bankAccounts = getBankAccounts(['E']);
        const trustAccounts = getTrustAccounts(['G', 'M'], field);

        return [...trustAccounts, ...bankAccounts].filter(v => v.data.balance >= (currentTx.amount || 0));
    };

    /**
     * List trust accounts for each client and matter, along with the operating account.
     *
     * @param {Transaction} tr
     * @param {'to'|'from'} [field]
     * @return {object[]} - formatted objects with label, value and data
     */
    const getTransferAccounts = (tr: Transaction, field: 'to' | 'from' = 'from'): SelectOption<string, ResultData>[] => {
        const bankAccounts = getBankAccounts([]);
        const trustAccounts = getTrustAccounts(['G', 'M'], field);

        return [...trustAccounts, ...bankAccounts];
    };

    /**
     *
     * @param {Transaction} tr
     * @returns {Escrow[]}
     */
    const getEscrowAccounts = (tr: Transaction) => {
        // @ts-expect-error - This is not ready yet
        return props.escrows.filter(e => tr.matterIds.includes(e.matterId));
    };

    /**
     * List trust accounts for each client and matter, along with the operating account.
     * Only called for withdrawals, not payments or transfers.
     */
    const getPayees = (): SelectOption<uuid, { id: uuid, type: 'C' }>[] => {
        return props.contacts
            .map(c => {
                const icon = c.isCompany ? Icons.BUSINESS : Icons.CONTACT;
                const name = <IconButton type={icon} label={formatLFM(c, true)}/>;

                return {
                    label: name,
                    value: c.id,
                    data: {
                        id: c.id,
                        type: 'C'
                    }
                };
            });
    };

    /**
     * Get a list of matters.
     *
     * For TT, only show clients that have funds in trust (matter or general).
     *
     * @param {Transaction} tr
     * @return {object[]} - formatted objects with label, value and data
     */
    const getClients = (tr) => {
        // TODO: This is all incorrect for type ESCROW!
        // Get a unique list of all clients
        const matterClients = (CaseHelper.getClients(true) as uuid[])
            .filter(cid => tr ? ['PT', 'PC'].includes(tr.txType) ? FinancialHelper.getTrustAmount('A', cid) > 0 : true : true);

        const leadClients = isEscrow(tr?.txType) || props.txType === 'TT' ? [] : util.flatten(props.leads.map(l => l.clients));

        const getIcon = (c) => {
            if (matterClients.includes(c.id)) return Icons.CASE;
            else return Icons.LEAD;
        };

        const getColor = (c) => {
            if (matterClients.includes(c.id)) return '#b3953c';
            else return 'green';
        };

        const clients = matterClients.concat(leadClients)
            .filter(util.distinctFilter)
            .map(cid => props.contacts.get(cid))
            .sort(sortContacts)
            .map(c => {
                const name = formatLFM(c);
                return {
                    label: <IconButton type={getIcon(c)} color={getColor(c)} label={name}/>,
                    value: name,
                    data: c
                };
            });

        // Filter based on state, rather than the transaction
        if (tr) {
            // Only return entries for the clients in state
            return clients.filter(c => tr.clientIds.includes(c.data.id));
        }
        else return clients;
    };

    /**
     * Get a list of matters.  Only show matters where the selected clients are available.
     *
     * @param {Transaction} tr
     * @return {SelectOption<string, Matter>[]} - formatted objects with label, value and data
     */
    const getMatters = (tr: Transaction): SelectOption<string, Matter>[] => {
        // Find all matters related to these clients.
        const matters: SelectOption<string, Matter>[] = props.matters
            // Matter must include every client in clientIds
            .filter(m => currentTx.clientIds.every(cid => m.clients.includes(cid)))
            .map(m => {
                return {
                    label: m.name,
                    value: m.name,
                    data: m
                };
            });

        if (tr) {
            return matters.filter(m => tr.matterIds.includes(m.data.id));
        }
        else return matters;
    };

    /**
     *
     * @param {Array.<{label: string, value: string, data: Transaction}>} values
     */
    const onClientsChange = (values) => {
        const current = currentTx.clone();

        // We always set the clientIds field
        current.clientIds = values.map(v => v.data.id);

        // If the client is also the source, we need to set the sourceType and source
        // if(['FR', 'PC'].includes(v.data.txType)) {
        //     current.sourceType = 'C';
        //     current.source = v.data.id;
        // }


        props.onChange(current);
    };

    /**
     * Called when asking how funds to multiple matters should be distributed.
     *
     * If there are multiple clients, it can NOT go into a Client Trust!
     *
     * @param {Transaction} [tr]
     * @return {Array.<{label: string, value: string}>}
     */
    const getDistributeTo = (tr) => {
        const values = [
            'Client Trust',
            'Matter Trusts',
            'Operating Account'
        ].map(v => {
            return {
                label: v,
                value: v[0] === 'O' ? 'B' : v[0]
            };
        });

        let results;
        if (tr) results = [values[tr.targetType === 'G' ? 0 : tr.targetType === 'M' ? 1 : 2]];
        else results = values;

        // if (currentTx.clientIds.length > 1) {
        //     // Filter out Client Trust
        //     return results.filter(v => v.label !== 'Client Trust');
        // }
        // else return results;
        return results;
    };

    const handlePaymentDistribution = (_e: SyntheticEvent, _matter: Matter) => {
        // TODO: Implement this properly
    }

    /**
     * Get the payment distribution (if available)
     *
     * @param {boolean} isP
     * @param {Transaction} tr
     * @param {FormElement} element
     * @returns {*}
     */
    const getPaymentDistribution = (isP, tr, element) => {
        if (isP && tr.targetType !== 'M' && tr.matterIds.length > 1) {
            // Determine the split.  Need an entry for each matter.
            const entries = tr.matterIds.map(mid => {
                const matter = props.matters.find(m => m.id === mid);
                let entry = tr.paymentDistribution ? tr.paymentDistribution.find(entry => entry.matterId === matter.id) : null;

                return [
                    <div key={`${matter.id}-amount`}
                         className='transaction-card__paymentDistribution-entry-amount'>
                        <div className="dollar-sign">$</div>
                        <input className='money'
                               value={entry ? entry.amount : 0}
                               type="number" step="0.01" min="-9999999999.99" max="9999999999.99"
                               required={true}
                               onChange={(e) => handlePaymentDistribution(e, matter)}
                        />
                    </div>,
                    <div key={`${matter.id}-name`}
                         className='transaction-card__paymentDistribution-entry-matter'>
                        {matter.name}
                    </div>
                ];
            }).flat();

            return element.renderElement('paymentDistribution', 'Distribution', (<>{entries}</>));
        }
        else return null;
    };

    /**
     * User has selected the distribution method, which is now the targetType.
     *
     * We can be smart and figure out the client trust.
     *
     * @param {string} value - the targetType ('G', 'M', 'B')
     */
    const onDistributeTo = (value) => {
        const current = currentTx.clone();
        current.targetType = value;

        // Default the client trust to the first client listed.
        if (value === 'G') current.target = current.clientIds[0];
        else if (value === 'B') {
            const accounts = getBankAccounts(['O']);
            if (accounts.length === 1) current.target = accounts[0].value;
        }


        props.onChange(current);
    };

    /**
     * Right now this only selects one value, because distributions is not capable of handling
     * multiple matters, each with its own source... when that is ready, switch over to the version
     * below.
     *
     * @param {{label: string, value: string, data: Matter}} values
     */
    const onMattersChange = (values) => {
        const current = currentTx.clone();

        const isP = isPayment(props.txType);

        if (isP) {
            if (values) current.matterIds = Array.isArray(values) ? values.map(v => v.data.id) : values.data.id;
            else current.matterIds = [];
        }
        else current.matterIds = values ? [values.data.id] : [];


        props.onChange(current);
    };

    const onSourceChange = (value: SelectOption<string, ResultData>) => {
        const current = currentTx.clone();
        current.source = value.data.id;
        current.sourceType = value.data.type;

        props.onChange(current);
    };

    /**
     * Change in the target of the funds.  Need to update txType here!
     *
     * @param {{label: string, value: string, data: {id: uuid, type: string, name: string, balance: number}}} value
     */
    const onTargetChange = (value) => {
        const current = currentTx.clone();
        const targetType = value.data.type;

        current.target = value.data.id;
        current.targetType = targetType;
        // FIXME: Uncomment this line once we have restored escrows
        // if (isEscrow(targetType)) current.escrow = true;


        props.onChange(current);
    };

    /**
     * @param {Date} dt
     */
    const onTransactionDateChanged = (dt: Date) => {
        const current = currentTx.clone();
        current.txDate = dt;

        props.onChange(current);
    };

    /**
     * @param {Date} dt
     */
    const onProcessFundsDate = (dt: Date) => {
        const current = currentTx.clone();
        current.processFundsDate = dt;

        props.onChange(current);
    };

    /**
     * @param {Date} dt
     */
    const onFundsAvailableDate = (dt: Date) => {
        const current = currentTx.clone();
        current.fundsAvailableDate = dt;

        props.onChange(current);
    };

    /**
     * @param {Date} dt
     */
    const onRefusedDate = (dt: Date) => {
        const current = currentTx.clone();
        current.refusedDate = dt;

        props.onChange(current);
    };

    /**
     * Refused checkbox clicked.
     */
    const onRefusedCheck = () => {
        const current = currentTx.clone();
        current.refusedDate = current.refusedDate ? null : new Date();

        props.onChange(current);
    };

    /**
     * @param {Date} dt
     */
    const onVoidedDate = (dt: Date) => {
        const current = currentTx.clone();
        current.voidedDate = dt;

        props.onChange(current);
    };

    /**
     * Voided checkbox clicked.
     */
    const onVoidedCheck = () => {
        const current = currentTx.clone();
        current.voidedDate = current.voidedDate ? null : new Date();

        props.onChange(current);
    };

    /**
     * @param {FormElement} element
     * @return {JSX.Element}
     */
    const renderClientName = (element) => {
        let label = 'Client';
        let value: string;

        if (props.client) {
            value = formatLFM(props.client, true);
        }
        else if (props.matter) {
            label = 'Clients';

            value = props.matter.clients
                .map(cid => formatFMLFromId(cid, false))
                .join(', ');
        }
        else {
            const clients = currentTx.clientIds.map(cid => formatFMLFromId(cid));
            if (clients.length > 1) label = i18n.cs('clients', true);
            value = clients.join(', ');
        }

        return element.renderElement('clientName', label, <div>{value}</div>, true);
    };

    /**
     * @param {FormElement} element
     * @return {*}
     */
    const renderMatterName = (element) => {
        return element.renderElement('matterName', 'Case', <div>{props.matter?.name || '???'}</div>, true);
    };

    // /**
    //  * See if the sub-transactions add up.
    //  *
    //  * @param {Transaction} tr
    //  * @returns {boolean}
    //  */
    // const addsUp = (tr) => {
    //     if (tr.matterIds?.length > 1 && tr.targetType === 'M') {
    //         // Ensure each matter has an entry, and that they all add up to Amount
    //         if (!tr.paymentDistribution) return false;
    //         else if (tr.paymentDistribution.length !== tr.matterIds.length) return false;
    //         else return tr.paymentDistribution.reduce((acc, cur) => acc + Number.parseFloat(cur.amount), 0) === tr.amount;
    //     }
    //     else return true;
    // };

    const element = props.element;
    const tr = currentTx;
    const txType = props.txType;

    // Used to hide/show parts of the UI based on the transaction type
    const isP = true;
    const isW = false;
    const isT = false;

    // See if there is sufficient funds to make the transaction. Applies to PC/PT payments only
    let sufficientFunds = true;
    if (['PT', 'PC', 'TT'].includes(txType) && tr.source && tr.amount) {
        // Check the source to see if there are enough funds for the amount requested
        sufficientFunds = FinancialHelper.getTrustAmount(tr.sourceType, tr.source) >= tr.amount;
    }

    // We can now determine if we are clear to save.
    const disabled =
        !sufficientFunds ||
        !(isTransfer(tr.txType) ? tr.source : true) ||
        !(isPayment(tr.txType) ? tr.paymentMethod : true) ||
        !(tr.isValid());

    // Handy for figuring out why we can't save
    // console.log(`addsUp ${addsUp}, sufficientFunds: ${sufficientFunds}, payments: ${(isPayment(tr.txType) ? tr.paymentMethod : true)}, ${tr.validationError}`);

    // Whether or not to show the target account is complicated.
    let showTargetAccount = isP || isT; // T is always yes, P has conditions where we can hide it

    /* Can we guess the target?
     * 1 Client, 2 Matters, targetType C or M:  Yes
     * 1 Client, 2 Matters, targetType O, 1 Operating Account: Yes
     * 2 Clients, 2 Matters, targetType M: Yes
     */
    // If we can be certain that there is only one client trust, and we have chosen a client trust, we can hide it
    if (isP && tr.matterIds.length > 1) {
        if (tr.clientIds.length === 1 && (tr.targetType === 'G' || tr.targetType === 'M')) showTargetAccount = false;
        if (tr.clientIds.length > 1 && tr.targetType === 'M') showTargetAccount = false;
    }

    // Payments only -- determine the payment distribution
    let paymentDistribution = getPaymentDistribution(isP, tr, element);

    if (props.readOnly) return (
        <>Need to make a read-only version, but only after weeding out what we need below</>
    );
    else return (
        <>
            {/* Transaction Date */}
            {element.renderDateTime('txDate', 'Date', onTransactionDateChanged, false, true)}

            {/* Client(s)
                 *
                 * -- Always show! --
                 *
                 * Allow multiple clients ONLY if they both exist within the same case.  That means we will need to
                 * filter available contacts based on clients in cases.
                 *
                 * If we are passed a client or matter, that means we are within the Contacts or Cases page,
                 * and we need to be restricted to those entities.
                 */}
            {(props.client || props.matter)
             ? renderClientName(element) // Just render the client name, no choices
             : element.renderSelect('clients',
                    <HelpMessage module='transaction' field='clients'/>,
                    true, false, getClients, onClientsChange, true)
            }

            {/* Matter(s) */}
            {props.matter && renderMatterName(element)}
            {!props.matter && (tr.clientIds.length > 0) && !isT &&
                element.renderSelect('matterIds',
                    <HelpMessage module='transaction' field='matterIds'/>,
                    isP, !isP, getMatters, onMattersChange, true,
                    isP ? {placeholder: 'Leave empty for payment to general trust'} : {})
            }

            {/* If more than one matter, ask if it will go into case trusts, a client trust, or an operating account */}
            {isP && tr.matterIds.length > 1 && element.renderSelect('targetType', 'Deposit To',
                false, false, getDistributeTo, onDistributeTo, false)}

            {/* Source */}
            {!isP && element.renderSelect('source',
                <HelpMessage module='transaction' field='fromAccount'/>,
                false, false, getSourceAccounts, onSourceChange, true)}

            {['FR', 'PC'].includes(txType) && element.renderSelect('source',
                <HelpMessage module='transaction' field='payer'/>,
                false, false, getContacts, onSourceChange, true)}

            {/* Amount */}
            {element.renderInput('amount', 'Amount', 'money', true)}

            {/* If a split, and we are putting into case trusts, ask for breakdown */}
            {isP && tr.matterIds.length > 1 && tr.targetType !== 'G' && paymentDistribution}

            {/* Target */}
            {(isT || isP) && showTargetAccount && element.renderSelect('target',
                <HelpMessage module='transaction' field='toAccount'/>,
                false, false, getTargetAccounts, onTargetChange, true)
            }
            {isW && element.renderSelect('target',
                <HelpMessage module='transaction' field='recipient'/>,
                false, false, getTargetAccounts, onTargetChange, true)
            }

            {/* An optional reference number */}
            {isP && element.renderInput('referenceNumber',
                <HelpMessage module='transaction' field='referenceNumber'/>,
                'text', false)}

            {/* Process Funds Date */}
            {isP && element.renderDate('processFundsDate',
                <HelpMessage module='transaction' field='processOn'/>,
                onProcessFundsDate)}

            {/* Funds Available Date */}
            {isP && element.renderDate('fundsAvailableDate',
                <HelpMessage module='transaction' field='fundsAvailable'/>,
                onFundsAvailableDate)
            }

            {/* Refused Date */}
            {isP && element.renderDateWithCheckbox('refusedDate',
                <HelpMessage module='transaction' field='refusedOn'/>,
                false, onRefusedCheck, onRefusedDate)
            }

            {/* Voided Date */}
            {isW && element.renderDateWithCheckbox('voidedDate',
                <HelpMessage module='transaction' field='voidedOn'/>,
                false, onVoidedCheck, onVoidedDate)
            }

            {/* Notes */}
            {element.renderMultilineTextOnly('notes', 'Notes', false)}

            {/* Button bar */}
            {(props.onOk && props.onCancel) &&
				<OkCancel label={i18n.ls('transaction', 'title')}
				          disabled={!!disabled}
				          onCancel={props.onCancel}
				          onSubmit={props.onOk}
				          onTop={true}
                    // likesEntityType='transaction'
                    // likesEntityId={tr.id}
				/>
            }
        </>
    );
}

export type EscrowCardProps = TransactionChildCardProps & Pick<RootState, 'bankAccounts' | 'contacts' | 'leads' | 'matters' | 'transactions'>;

type ResultData = { id: string, type: string, name: string | ReactElement, balance: number };

const mapStateToProps = (state: RootState) => {
    return {
        bankAccounts: state.bankAccounts,
        contacts: state.contacts,
        // escrows: state.escrows,
        leads: state.leads,
        matters: state.matters,
        transactions: state.transactions,
    };
};

export default connect(mapStateToProps)(EscrowCard);
